import { FC } from 'react';
import { Form, InputNumber as AntInputNumber, InputNumberProps } from 'antd';
import cn from 'classnames';
import { Field, FieldProps } from 'formik';
import type { ValueType } from '@rc-component/mini-decimal';

import { FormFieldProps, FormikFieldProps } from '../Formik';

import './index.scss';
import styles from './index.module.scss';

export const InputNumber = ({ className, ...otherProps }: InputNumberProps) => {
	return <AntInputNumber {...otherProps} className={cn(styles.input, className)} />;
};

type Props = FormFieldProps<Omit<InputNumberProps, 'form'>>;

export const FormInputNumber: FC<Props> = ({
	field: { value, onChange, ...field },
	form: { errors, setFieldValue, setFieldError },
	label,
	size,
	...otherProps
}: Props) => {
	const errorMsg = errors[field.name] as string;

	const handleFocus = () => {
		setFieldError(field.name, undefined);
	};

	const parsedValue = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : undefined;

	const handleChange = (val: ValueType | null) => {
		if (typeof val === 'number') {
			const sanitizedValue = Math.max(0, Math.floor(val));
			setFieldValue(field.name, sanitizedValue);
		} else {
			setFieldValue(field.name, 0);
		}
	};

	return (
		<Form.Item
			label={label}
			help={errorMsg}
			shouldUpdate
			validateStatus={errorMsg ? 'error' : undefined}
			colon={false}>
			<AntInputNumber
				{...field}
				{...otherProps}
				value={parsedValue}
				min={0}
				step={1}
				type="number"
				onFocus={handleFocus}
				onChange={handleChange}
				onKeyDown={e => {
					const invalidChars = ['-', '+', 'e', 'E', '.'];
					if (invalidChars.includes(e.key)) {
						e.preventDefault();
					}
				}}
				onBlur={() => {
					if (typeof parsedValue === 'number') {
						const sanitized = Number(parsedValue.toString());
						setFieldValue(field.name, sanitized);
					}
				}}
				size={size}
			/>
		</Form.Item>
	);
};

type FormikProps = FormikFieldProps<InputNumberProps>;

export const FormikInputNumber: FC<FormikProps> = ({ name, ...otherProps }: FormikProps) => {
	return (
		<Field name={name}>
			{({ field, form, meta }: FieldProps) => (
				<FormInputNumber field={field} form={form} meta={meta} {...otherProps} />
			)}
		</Field>
	);
};
